import { api } from './api';
import type { User } from '../contexts/AuthContext';

// 登录响应接口
export interface LoginResponse {
  access_token: string;
  user: User;
}

// 注册响应接口
export interface RegisterResponse {
  access_token: string;
  user: User;
}

// 登录请求数据
export interface LoginRequest {
  phone?: string;
  email?: string;
  password?: string;
  verification_code?: string;
  remember_me?: boolean; // 是否保持登录（30天）
}

// 注册请求数据
export interface RegisterRequest {
  phone: string;
  password: string;
  verification_code: string;
  username?: string;
  gender?: string;
}

// 邮箱注册请求数据
export interface RegisterEmailRequest {
  email: string;
  password: string;
  verification_code: string;
  username?: string;
  phone?: string;
  gender?: string;
}

// 发送验证码请求
export interface SendCodeRequest {
  phone: string;
  slide_token?: string;
  purpose?: 'register' | 'reset_password' | 'update_phone';  // 发送目的
}

// 发送邮箱验证码请求
export interface SendEmailCodeRequest {
  email: string;
  slide_token?: string;
  purpose?: 'register' | 'reset_password' | 'update_email';  // 发送目的
}

// 用户认证API服务
export const authApi = {
  // 密码登录（支持手机号或邮箱）
  loginWithPassword: async (data: { phone?: string; email?: string; password: string; remember_me?: boolean }): Promise<LoginResponse> => {
    // 确保至少有一个账号字段
    if (!data.phone && !data.email) {
      throw new Error('请提供手机号或邮箱');
    }
    return api.post<LoginResponse>('/auth/login', data);
  },

  // 验证码登录（手机号）
  loginWithCode: async (data: { phone: string; verification_code: string }): Promise<LoginResponse> => {
    return api.post<LoginResponse>('/auth/login-phone', data);
  },

  // 验证码登录（邮箱）
  loginWithEmailCode: async (data: { email: string; verification_code: string }): Promise<LoginResponse> => {
    return api.post<LoginResponse>('/auth/login-email', data);
  },

  // 用户注册（手机号）
  register: async (data: RegisterRequest): Promise<RegisterResponse> => {
    return api.post<RegisterResponse>('/auth/register', data);
  },

  // 用户注册（邮箱）
  registerWithEmail: async (data: RegisterEmailRequest): Promise<RegisterResponse> => {
    return api.post<RegisterResponse>('/auth/register-email', data);
  },

  // 发送验证码（手机号）
  sendVerificationCode: async (data: SendCodeRequest): Promise<{ message: string; debug_code?: string; phone_needs_transfer?: boolean }> => {
    return api.post<{ message: string; debug_code?: string; phone_needs_transfer?: boolean }>('/auth/send-code', data);
  },

  // 发送验证码（邮箱）
  sendEmailVerificationCode: async (data: SendEmailCodeRequest): Promise<{ message: string; debug_code?: string }> => {
    return api.post<{ message: string; debug_code?: string }>('/auth/send-email-code', data);
  },

  // 获取用户信息
  getProfile: async (): Promise<User> => {
    const response = await api.get<{ user: User }>('/auth/profile');
    return response.user;
  },

  // 更新用户信息
  updateProfile: async (data: Partial<User>): Promise<{ message: string; user: User; phone_transferred?: boolean }> => {
    return api.put<{ message: string; user: User; phone_transferred?: boolean }>('/auth/profile', data);
  },

  // 重置密码（手机号）
  resetPassword: async (data: { phone: string; password: string; verification_code: string }): Promise<{ message: string }> => {
    return api.post<{ message: string }>('/auth/reset-password', data);
  },

  // 重置密码（邮箱）
  resetPasswordWithEmail: async (data: { email: string; password: string; verification_code: string }): Promise<{ message: string }> => {
    return api.post<{ message: string }>('/auth/reset-password-email', data);
  },

  // 修改密码（需要原密码）
  changePassword: async (data: { old_password: string; new_password: string }): Promise<{ message: string }> => {
    return api.post<{ message: string }>('/auth/change-password', data);
  },

  // 通过手机号验证码修改密码（不需要传递手机号，后端自动使用当前用户的手机号）
  changePasswordWithPhone: async (data: { new_password: string; verification_code: string }): Promise<{ message: string }> => {
    return api.post<{ message: string }>('/auth/change-password-with-phone', data);
  },

  // 发送验证码用于修改密码（自动使用当前用户的手机号）
  sendCodeForPasswordChange: async (data: { slide_token: string }): Promise<{ message: string; debug_code?: string }> => {
    return api.post<{ message: string; debug_code?: string }>('/auth/send-code-for-password-change', data);
  },

  // 微信扫码登录 - 获取二维码
  getWechatQrcode: async (): Promise<{ scene: string; qrcode: string; expires_in: number }> => {
    return api.get<{ scene: string; qrcode: string; expires_in: number }>('/auth/wechat/qrcode');
  },

  // 微信扫码登录 - 检查扫码状态
  checkWechatScan: async (scene: string): Promise<{ status: string; access_token?: string; user?: User }> => {
    return api.post<{ status: string; access_token?: string; user?: User }>('/auth/wechat/check-scan', { scene });
  },

  // 小程序端微信登录
  wechatMiniprogramLogin: async (data: { code: string; scene?: string; userInfo?: any }): Promise<LoginResponse> => {
    return api.post<LoginResponse>('/auth/wechat/miniprogram-login', data);
  },
};

// 便捷导出：发送验证码（手机号）
export const sendVerificationCode = authApi.sendVerificationCode;
